import { Component, OnInit, AfterViewInit, Renderer2, ViewChild } from '@angular/core';
import { LoaderService } from 'src/app/service/shared/loader/loader.service';
import { TableData } from 'src/app/shared/models/tableData';
import { JobPointeSettingsService } from 'src/app/service/job-pointe-settings/job-pointe-settings.service';
import { JobPointePermissionsService } from 'src/app/service/auth/job-pointe-permission.service';
import { JobPointeTransferService } from '../job-pointe-transfer/job-pointe-transfer.service';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'app-employees',
  templateUrl: './employees.component.html',
  styleUrls: ['./employees.component.scss'],
})
export class EmployeesComponent implements OnInit, AfterViewInit {
  constructor(
    private jobPointeSettingsService: JobPointeSettingsService,
    public renderer2: Renderer2,
    private loaderService: LoaderService,
    private jobPointePermissionsService: JobPointePermissionsService,
    private JobPointeTransferService: JobPointeTransferService,
  ) { }

  ngOnInit(): void {
    this.loadSelect2();
  }

  refreshEmployeesSource = new BehaviorSubject<boolean>(false);
  refreshEmployees = this.refreshEmployeesSource.asObservable();

  modelDetail: any = {
    'asset-classes': {},
    categories: {},
    manufacturers: {},
    enabled: {},
  };

  mapJobBoardJob(job) {
    if (!job?.job) return false;
    if (job['job'].name) return `${job.job?.jobNumber} - ${job.job.name.trim()}`;
    if (job['facility'].owner?.ownerName) return job?.facility?.owner?.ownerName.trim();
    if (job['facility'].facilityName) return job?.facility?.facilityName.trim();
    return false;
  }

  getFirstCurrentAssignment(row: Record<string, unknown>): string {
    const assignments = row.currentAssignment as string;
    if (!assignments) return '';

    return assignments.split(',')[0];
  }

  mapCurrentAssignmentTooltip(row: Record<string, unknown>): string {
    const assignments = row.currentAssignment as string;
    if (!assignments) return '';

    return assignments.replace(',', ',  ');
  }

  data: TableData = {
    label: 'Employees',
    type: 'employees',
    editAccess: true,
    statusToggle: false,
    columns: [
      {
        name: 'employeeAlias',
        label: 'Name',
        component: 'employee-profile',
        property: () => '',
        sortable: true,
        columnStyles: { 
          'max-width': '200px', 
          padding: '0',
          'white-space': 'normal',
          'word-wrap': 'break-word'
        },
        headerStyles: { 'min-width': '6rem' },
        columnClasses: { 'text-truncate': false },
        show: row => {
          return this.jobPointePermissionsService.isAllowed(row, 'job_pointe_employees', 'view');
        },
      },
      {
        name: 'unionLocal',
        label: 'Union',
        property: row => (row?.employeeUnionLocal ? `${row?.employeeUnionLocal?.name}` : ''),
        sortable: true,
        columnStyles: { 'max-width': '140px', 'white-space': 'inherit' },
        headerStyles: { 'min-width': '4rem' },
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'department',
        label: 'Department',
        property: row => `${row?.employeeDepartment?.shortCode || 'N/A'}`,
        sortable: true,
        columnStyles: { 'max-width': '110px' },
        headerStyles: { 'min-width': '6rem' },
        columnClasses: { 'text-truncate': false },
      },
      {
        name: 'employeeTitle.name',
        label: 'Title',
        property: row => `${row?.employeeTitle?.name || 'N/A'}`,
        sortable: true,
        columnStyles: { 
          'max-width': '150px',
          'white-space': 'normal',
          'word-wrap': 'break-word'
        },
        headerStyles: { 'min-width': '8rem' },
        columnClasses: { 'text-truncate': false },
      },
      /* tslint:disable-next-line curly */
      {
        name: 'city',
        label: 'City',
        property: row => row?.city,
        columnStyles: { 'max-width': '120px' },
        headerStyles: { 'min-width': '9rem' },
        sortable: true,
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'currentAssignment',
        label: 'Current Assignment(s)',
        property: row => this.getFirstCurrentAssignment(row),
        link: () => false,
        show: () => true,
        tooltip: row => this.mapCurrentAssignmentTooltip(row),
        sortable: true,
        columnStyles: {
          'min-width': '100px',
          'max-width': 'fit-content',
          'text-align': 'left',
          'text-overflow': 'ellipsis',
        },
        headerStyles: { 'max-width': '15rem' },
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'status',
        label: 'Status',
        component: 'employee-status',
        property: row => row?.status,
        sortable: true,
        columnStyles: { 'max-width': '110px' },
        headerStyles: { 'min-width': '5rem', 'text-align': 'center' },
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'isOnJobBoard',
        label: 'Labor Board',
        property: row => row?.isOnJobBoard,
        sortable: true,
        selectable: true,
        checkboxDisabled: false,
        columnStyles: { 'max-width': '110px', 'text-align': '-webkit-center' },
        headerStyles: { 'min-width': '5rem', 'text-align': 'center' },
        columnClasses: { 'text-truncate': false },
        show: row => {
          return this.jobPointePermissionsService.isAllowed(row, 'job_pointe_employees', 'edit');
        },
      },
    ],
    rowActions: [
      {
        label: 'User',
        actionId: 'openEmpProfilePopup',
        imageUrl: '',
        iconName: 'fa fa-solid fa-user',
        show: () => true,
      },
    ],
    data: [],
    pagination: {},
    meta: {},
  };

  loadOptions(key: string): void {
    // loop through modelDetails object and capture information from API
    // call the API for this endpoint 'key' and store result in modelDetail[key]
    // let defaultParams = {...(key === 'tools' && {order: 'toolId'} ) }

    this.jobPointeSettingsService.read[key]().subscribe(
      res => {
        // this.loaderService.hideLoader();
        const settings = res.data.reduce((list, item) => {
          list[item.id] = item.name;
          return list;
        }, {});

        this.modelDetail[key] = settings;
        // establish the list of options for the select elements
        const filter = this.filters.find(f => f.model === key);

        // Sorting the options alphabetically by the name property
        filter.options = res.data.sort((a, b) => {
          const nameA = a.name ? a.name.toLowerCase() : '';
          const nameB = b.name ? b.name.toLowerCase() : '';
          return nameA.localeCompare(nameB);
        });

        //Handle static options
        filter.options = filter?.staticOptions ? [...filter.staticOptions, ...res.data] : res.data;

        //Handle field prefix
        filter.options = filter?.prefixField
          ? filter.options.map(d => {
            d[filter.name] = d?.[filter.prefixField]
              ? `${d[filter.prefixField]} - ${d[filter.name]}`
              : d[filter.name];
            return { ...d };
          })
          : filter.options;
      },
      error => {
        console.error(error);
        this.loaderService.hideLoader();
      },
    );
  }

  ngAfterViewInit(): void {
    this.loadSelect2();
  }

  loadSelect2() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `loadSelect2();`;
  }

  filters: Array<any> = [
    {
      label: 'Union',
      type: 'select2',
      propertyName: 'scope[local]',
      default: '',
      value: 'union_local_id',
      name: 'description',
      prefixField: 'union_local_id',
      model: 'locals',
      staticOptions: [
        { union_local_id: 'All', description: 'Unions' },
        { union_local_id: 'Non', description: 'Unions' },
      ],
      options: [],
      loadOptions: () => this.loadOptions('locals'),
    },
    {
      label: 'Department',
      type: 'select2',
      propertyName: 'scope[department]',
      default: '',
      value: 'id',
      name: 'department_name',
      model: 'departments',
      options: [],
      loadOptions: () => this.loadOptions('departments'),
    },
    {
      label: 'Title',
      type: 'select2',
      propertyName: 'scope[employeeTitle]',
      default: '',
      value: 'id',
      name: 'name',
      model: 'employeeTitles',
      options: [],
      loadOptions: () => this.loadOptions('employeeTitles'),
    },
    {
      label: 'Owner',
      type: 'select2',
      propertyName: 'scope[jobBoardFacilityOwners]',
      default: '',
      value: 'id',
      name: 'ownerName',
      model: 'owner',
      options: [],
      loadOptions: () => this.loadOptions('owner'),
    },
    {
      label: 'Facility',
      type: 'autocomplete',
      propertyName: 'scope[jobBoardFacilities]',
      default: '',
      value: 'id',
      name: 'facilityName',
      model: 'facility',
      options: [],
      loadOptions: () => this.loadOptions('facility'),
    },
    {
      label: 'Status',
      type: 'select2',
      propertyName: 'scope[status]',
      default: '',
      value: 'id',
      name: 'label',
      model: 'status',
      options: [
        { id: 1, label: 'Active', selectedByDefault: true },
        { id: 'false', label: 'Inactive' },
      ],
    },
    {
      label: 'Employee Group',
      type: 'select2',
      propertyName: 'scope[employeeGroup]',
      default: '',
      value: 'id',
      name: 'displayName',
      model: 'employeeGroups',
      options: [],
      loadOptions: () => this.loadOptions('employeeGroups'),
    },
  ];
}
